import _ from 'lodash'
import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts/index'
import gameIframe from '@ps/bridge/iframe/iframe'
import i18n from 'i18n'
import {
  isItalic,
  setEnableGlow,
  setItalic,
  styleAttrsNeedToSeparate,
} from '../node/text'
import { getDescendantsText } from '@ps/bridge/qc/node/utils'
import { setEnableShadowAfterSetFont } from '@ps/bridge/qc/node/text'
import { getSceneNodeByUuid } from '@ps/bridge/qc/base'
import {
  setUnSplitPropValue,
  removeUnSplitPropValue,
} from '@ps/bridge/qc/scripts/editor'
export interface LanguageScriptText {
  name: string
  value: string
  scene?: string
  style: {
    [key: string]: any
  }
}

export interface LanguageScriptLanguage {
  [adapterResolution: string]: LanguageScriptText
}

export interface LanguageScriptLanguageList {
  [lang: string]: LanguageScriptLanguage
}

export interface LanguageScript {
  langConfig: LanguageScriptLanguageList
  // 保存时，langKey 用作 languages.json 每个文本节点配置的 key
  // 多语言脚本与 languageMgr 是通过 langKey 去 languages.json 中获取对应的文本值
  langKey: string
}

export const getScript = (
  node: qc.Node,
  addScriptIfScriptNotExist = true,
): LanguageScript | void => {
  const config: LanguageScript = node.getScript(scripts.languages.name)
  if (config) {
    return config
  }
  if (addScriptIfScriptNotExist) {
    return node.addScript(scripts.languages.name) as LanguageScript
  }
}

export const removeScript = (node: qc.Node): void => {
  const config: LanguageScript = node.getScript(scripts.languages.name)
  if (config) {
    node.removeScript(config)
  }
}

export const updateLanguageTextValue = ({
  lang,
  text,
}: {
  lang: string
  text: {
    [key: string]: string
  }
}): void => {
  lang = lang.toLowerCase()
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  texts.forEach((node) => {
    const language = getScript(node) as LanguageScript
    _.set(language.langConfig, `${lang}.pdef.value`, text[node.uuid])
    _.set(language.langConfig, `${lang}.ldef.value`, text[node.uuid])
    // 兼容旧项目数据,旧转新
    delete language.langConfig[lang]!.value
  })
}

/**
 * 返回特定语言、特定方向的所有文本值列表
 */
export const getLanguageTextValue = (
  lang: string,
  adapterResolution: string,
): {
  [key: string]: string
} => {
  lang = lang.toLowerCase()
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  const config: {
    [key: string]: string
  } = {}
  texts.forEach((node) => {
    const language = getScript(node) as LanguageScript
    if (language.langConfig[lang]![adapterResolution]) {
      config[node.uuid] = language.langConfig[lang]![adapterResolution]!.value
    } else {
      // 兼容旧项目数据，旧数据缺少横竖屏不区分（adapterResolution）
      // @ts-ignore
      config[node.uuid] = language.langConfig[lang].value
    }
  })
  return _.cloneDeep(config)
}

/**
 * 返回节点树中特定语言、特定方向的所有文本与样式配置
 */
export const getLanguageConfig = (
  lang: string,
  adapterResolution: string,
): i18n.Language => {
  lang = lang.toLowerCase()
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  const config: i18n.Language = {}
  texts.forEach((textNode) => {
    const psLang = getScript(textNode) as LanguageScript
    if (psLang.langConfig[lang]![adapterResolution]) {
      config[textNode.uuid] = <i18n.Text>(
        _.cloneDeep(psLang.langConfig[lang]![adapterResolution])
      )
    } else {
      // 兼容旧项目数据
      // @ts-ignore
      config[textNode.uuid] = _.cloneDeep(psLang.langConfig[lang])
    }

    // 存储在多语言脚本上 langConfig 的场景名字可能不正确，所以重新获取一次场景名字
    // 如复制有文本节点的场景节点，粘贴的新场景节点下的文本节点的场景名字还是原来的场景名字
    // 如复制文本节点到另一个场景，如修改场景节点名字
    // 这几个操作都没有更新文本节点相关的场景名字
    const sceneNode = getSceneNodeByUuid(textNode.uuid)
    const sceneName = sceneNode ? sceneNode.uniqueName : ''
    if (sceneName) {
      config[textNode.uuid]!.scene = sceneName
    }
  })
  return _.cloneDeep(config)
}

/**
 * 新增语言，为每一个文本节点增加新的语言文本样式信息
 * @param lang 新语言
 * @param value 新语言配置信息
 */
export const addLanguage = ({
  lang,
  value,
}: {
  lang: string
  value: i18n.Language
}): void => {
  lang = lang.toLowerCase()
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  texts.forEach((node) => {
    setUnSplitPropValue(node, `fontSize${lang}`, true)
    const language = getScript(node) as LanguageScript
    // 新增语言，除文本外，其他配置用兜底语言的配置
    _.set(language.langConfig, `${lang}.pdef`, value[node.uuid])
    _.set(language.langConfig, `${lang}.ldef`, value[node.uuid])
  })
}

export const deleteLanguage = (lang: string): void => {
  lang = lang.toLowerCase()
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)
  texts.forEach((node) => {
    removeUnSplitPropValue(node, `fontSize${lang}`)
    const psLang = getScript(node) as LanguageScript
    delete psLang.langConfig[lang]
  })
}

export const refreshTextStyleByLangConfig = (
  node: qc.UIText,
  lang: string,
  adapterResolution: string,
) => {
  const qc = gameIframe.qc
  const psLang = getScript(node) as LanguageScript
  if (!psLang.langConfig[lang]) return
  let textValues = psLang.langConfig[lang]![adapterResolution]!
  // 兼容旧数据
  if (typeof textValues === 'undefined') {
    // @ts-ignore
    textValues = psLang.langConfig[lang]
  }
  node.text = String(textValues.value)
  for (const valueKey in textValues['style']) {
    const nodeValue = textValues['style'][valueKey]
    if (
      valueKey === 'startColor' ||
      valueKey === 'endColor' ||
      valueKey === 'glowColor' ||
      valueKey === 'shadowColor'
    ) {
      node[valueKey] = new qc.Color(nodeValue)
    } else if (valueKey === 'color') {
      const colorValue = textValues['style']['color']
      if (Array.isArray(colorValue) && colorValue.length < 4) {
        // rgba
        colorValue.push(1)
      }
      node['color'] = new qc.Color(colorValue)
    } else if (valueKey === 'strokeColor') {
      node['stroke'] = new qc.Color(nodeValue)
    } else if (valueKey === 'stroke') {
      node['strokeThickness'] = nodeValue as number
    } else if (valueKey === 'italic') {
      setItalic(node, nodeValue)
    } else if (valueKey === 'enableGlow') {
      // 由于enableGlow设置后，会充值italic属性，所以这边在
      setEnableGlow(node, nodeValue)
    } else {
      // @ts-ignore
      node[valueKey] = nodeValue
    }
  }
  setEnableShadowAfterSetFont(node)
}

export const refreshTextLanguage = (
  lang: string,
  adapterResolution: string,
): void => {
  const gamePlay = gameIframe.gamePlay
  const texts = getDescendantsText(gamePlay)

  texts.forEach((node) => {
    refreshTextStyleByLangConfig(node, lang, adapterResolution)
  })
}

/**
 * 获取languageConfig的一个文字配置的内容
 * @param {*} node 处理的目标节点
 * @param {*} adapter 若有值,则需要读取adpter横竖屏的数据
 * @param {*} lang 待处理的语言
 */
export const generateLanguageConfigTextByNode = (
  node: qc.UIText,
  adapter?: string,
  lang?: string,
) => {
  // 是从其他地方复制过来的代码，_rgb、_alpha 还没有加到类型声明中，为什么不用 rgb 与 alpha 暂时不清楚
  // 先使用 @ts-ignore 忽略 ts 报错
  // 注意：暂时没有提取 visible 的需求，之后加这个需求的时候要注意如果是临时节点的 visible 在 ps 项目中固定是 false，导出的时候需要将其转换为 true
  const config = {
    name: node.name,
    value: node.text,
    style: {
      fontSize: node.fontSize,
      color: node.color.rgb.concat(node.color.alpha),
      // alpha: node.alpha, 所有语言在新版 ps 中是共用一个透明度值，没必要单独区分
      stroke: node.strokeThickness,
      // @ts-ignore
      strokeColor: node.stroke._rgb.concat(node.stroke._alpha),
      bold: node.bold,
      italic: isItalic(node),
      enableShadow: node.enableShadow,
      // @ts-ignore
      shadowColor: node.shadowColor._rgb.concat(node.shadowColor._alpha),
      shadowBlur: node.shadowBlur,
      shadowOffsetX: node.shadowOffsetX,
      shadowOffsetY: node.shadowOffsetY,
      gradient: node.gradient,
      // @ts-ignore
      startColor: node.startColor._rgb.concat(node.startColor._alpha),
      // @ts-ignore
      endColor: node.endColor._rgb.concat(node.endColor._alpha),
      enableGlow: node.enableGlow,
      // @ts-ignore
      glowColor: node.glowColor._rgb.concat(node.glowColor._alpha),
      glowBlur: node.glowBlur,
      lineSpacing: node.lineSpacing,
      enableUnderline: node.enableUnderline,
      underlineColor: node.underlineColor,
      underlineWidth: node.underlineWidth,
      underlineSpacing: node.underlineSpacing,
      enableDeleteline: node.enableDeleteline,
      deletelineColor: node.deletelineColor,
      deletelineWidth: node.deletelineWidth,
      deletelineSpacing: node.deletelineSpacing,
      letterSpacing: node.letterSpacing,
      lineHeight: node.lineHeight,
      font: node.font,
    },
  }
  if (adapter) {
    // 对于需要横竖屏区分属性的数据,读取adapter的langConfig属性数据,防止被覆盖
    const language = node.getScript('ps.Languages')
    styleAttrsNeedToSeparate.forEach((prop) => {
      const value = _.get(
        language.langConfig,
        `${lang}.${adapter}.style.${prop}`,
      )
      if (typeof value !== 'undefined') {
        // @ts-ignore
        config.style[prop] = value
      }
    })
    return config
  }
  return config
}

/**
 * 更新节点语言脚本配置
 */
export const updateLanguageConfig = (
  node: qc.Node,
  value: {
    // 名字
    name: string
  },
) => {
  if (!_.isPlainObject(value)) return

  const { name } = value
  const lang = getScript(node) as LanguageScript
  for (const i in lang.langConfig) {
    if (lang.langConfig[i]!.pdef) {
      lang.langConfig[i]!['pdef']!.name = name
    }
    if (lang.langConfig[i]!.ldef) {
      lang.langConfig[i]!['ldef']!.name = name
    }
    // 兼容旧项目数据
    if (lang.langConfig[i]!.name) {
      // @ts-ignore
      lang.langConfig[i].name = name
    }
  }
}

/**
 * 更新脚本的 langKey 属性
 */
export const updateLangKey = (node: qc.Node, langKey: string) => {
  const lang = getScript(node)
  if (lang) {
    lang.langKey = langKey
  }
}
